import React from 'react';
import PropTypes from 'prop-types';

/**
 * ReadinessAttractivenessChart - Scatter plot showing business positioning
 * @param {Object} assessment - Assessment data with readiness and attractiveness scores
 * @param {boolean} isChartHovered - Whether the chart is being hovered
 * @param {function} setIsChartHovered - Handler to set hover state
 * @param {function} getScoreColor - Function to get color based on score
 */
const ReadinessAttractivenessChart = ({
  assessment,
  isChartHovered,
  setIsChartHovered,
  getScoreColor
}) => {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-300 p-3 sm:p-6 lg:p-8 shadow-xl">
      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
        Readiness & Attractiveness
      </h3>
      <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">Your business positioning for exit success</p>
      <div
        className="relative w-full h-64 sm:h-80 lg:h-96 bg-white rounded-xl pl-12 sm:pl-16 pr-4 sm:pr-8 pt-6 sm:pt-8 pb-10 sm:pb-12 shadow-inner border-2 border-gray-100"
        onMouseEnter={() => setIsChartHovered(true)}
        onMouseLeave={() => setIsChartHovered(false)}
      >
        <svg viewBox="-12 -2 118 108" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
          {/* Colored quadrants for visual zones */}
          <defs>
            <linearGradient id="topRightGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 0.15 }} />
              <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0.25 }} />
            </linearGradient>
            <linearGradient id="bottomLeftGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#ef4444', stopOpacity: 0.15 }} />
              <stop offset="100%" style={{ stopColor: '#ef4444', stopOpacity: 0.2 }} />
            </linearGradient>
            <radialGradient id="pointGlow">
              <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 0.8 }} />
              <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0 }} />
            </radialGradient>
          </defs>

          {/* Top-right quadrant (High Readiness, High Attractiveness) - Ideal Zone */}
          <rect x="50" y="0" width="50" height="50" fill="url(#topRightGradient)" />

          {/* Bottom-left quadrant (Low Readiness, Low Attractiveness) - Critical Zone */}
          <rect x="0" y="50" width="50" height="50" fill="url(#bottomLeftGradient)" />

          {/* Grid lines - more visible */}
          {[20, 40, 60, 80].map(val => (
            <React.Fragment key={val}>
              <line x1="0" y1={100-val} x2="100" y2={100-val} stroke="#d1d5db" strokeWidth="0.4" strokeDasharray="3,3" />
              <line x1={val} y1="0" x2={val} y2="100" stroke="#d1d5db" strokeWidth="0.4" strokeDasharray="3,3" />
            </React.Fragment>
          ))}

          {/* Center crosshair - more prominent */}
          <line x1="0" y1="50" x2="100" y2="50" stroke="#6b7280" strokeWidth="1" strokeDasharray="5,4" />
          <line x1="50" y1="0" x2="50" y2="100" stroke="#6b7280" strokeWidth="1" strokeDasharray="5,4" />

          {/* Axes - thicker and darker */}
          <line x1="0" y1="100" x2="100" y2="100" stroke="#1f2937" strokeWidth="1.5" />
          <line x1="0" y1="0" x2="0" y2="100" stroke="#1f2937" strokeWidth="1.5" />

          {/* Arrow heads for axes - larger */}
          <polygon points="100,100 96,98.5 96,101.5" fill="#1f2937" />
          <polygon points="0,0 1.5,4 -1.5,4" fill="#1f2937" />

          {assessment.overall_score > 0 && (
            <g>
              {/* Glow effect - larger */}
              <circle
                cx={assessment.readiness_score || 20}
                cy={100 - (assessment.attractiveness_score || 20)}
                r="10"
                fill="url(#pointGlow)"
              />
              {/* Outer ring - more visible */}
              <circle
                cx={assessment.readiness_score || 20}
                cy={100 - (assessment.attractiveness_score || 20)}
                r="6"
                fill="none"
                stroke="#3b82f6"
                strokeWidth="1"
                opacity="0.5"
              />
              {/* Main point - larger */}
              <circle
                cx={assessment.readiness_score || 20}
                cy={100 - (assessment.attractiveness_score || 20)}
                r="4"
                fill="#3b82f6"
                stroke="white"
                strokeWidth="2"
              />
              {/* Inner highlight */}
              <circle
                cx={assessment.readiness_score || 20}
                cy={100 - (assessment.attractiveness_score || 20)}
                r="1.8"
                fill="white"
                opacity="0.9"
              />
              {/* Crosshair lines to axes - more visible */}
              <line
                x1={assessment.readiness_score || 20}
                y1={100 - (assessment.attractiveness_score || 20)}
                x2={assessment.readiness_score || 20}
                y2="100"
                stroke="#3b82f6"
                strokeWidth="0.8"
                strokeDasharray="3,3"
                opacity="0.6"
              />
              <line
                x1="0"
                y1={100 - (assessment.attractiveness_score || 20)}
                x2={assessment.readiness_score || 20}
                y2={100 - (assessment.attractiveness_score || 20)}
                stroke="#3b82f6"
                strokeWidth="0.8"
                strokeDasharray="3,3"
                opacity="0.6"
              />
            </g>
          )}

          {/* Axis labels - larger and bolder */}
          {[0, 25, 50, 75, 100].map(val => (
            <React.Fragment key={`label-${val}`}>
              <text x={val} y="105" fontSize="3.5" fill="#374151" textAnchor="middle" fontWeight="600">{val}</text>
              <text x="-5" y={102-val} fontSize="3.5" fill="#374151" textAnchor="end" fontWeight="600">{val}</text>
            </React.Fragment>
          ))}
        </svg>

        {/* Axis titles - same size and style */}
        <div className="absolute bottom-0 left-12 sm:left-16 right-4 sm:right-8 text-center">
          <p className="text-xs sm:text-sm font-bold text-gray-800">Exit Readiness →</p>
        </div>
        <div className="absolute -left-2 sm:-left-3 top-6 sm:top-8 bottom-10 sm:bottom-12 flex items-center justify-center" style={{ width: '60px' }}>
          <div className="transform -rotate-90 origin-center">
            <p className="text-xs sm:text-sm font-bold text-gray-800 whitespace-nowrap">Business Attractiveness →</p>
          </div>
        </div>

        {/* Score display - hover to show, with transparency */}
        {assessment.overall_score > 0 && isChartHovered && (
          <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-white bg-opacity-95 rounded-xl shadow-lg px-2 sm:px-4 py-2 sm:py-3 border-2 border-blue-300 transition-opacity duration-200">
            <div className="text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Current Position</div>
            <div className="space-y-1 sm:space-y-1.5">
              <div className="flex items-center justify-between gap-2 sm:gap-4">
                <span className="text-xs sm:text-sm text-gray-600 font-medium">Readiness:</span>
                <span
                  className="text-sm sm:text-lg font-bold"
                  style={{ color: getScoreColor(assessment.readiness_score || 0) }}
                >
                  {Math.round(assessment.readiness_score || 0)}%
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 sm:gap-4">
                <span className="text-xs sm:text-sm text-gray-600 font-medium">Attractiveness:</span>
                <span
                  className="text-sm sm:text-lg font-bold"
                  style={{ color: getScoreColor(assessment.attractiveness_score || 0) }}
                >
                  {Math.round(assessment.attractiveness_score || 0)}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Legend for quadrants - larger text */}
      <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
        <div className="flex items-center gap-2 p-2 sm:p-3 bg-white rounded-lg border-2 border-green-200 shadow-sm">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-green-100 border-2 border-green-400 flex-shrink-0"></div>
          <span className="text-gray-700"><strong>Ideal Zone:</strong> High readiness & attractiveness</span>
        </div>
        <div className="flex items-center gap-2 p-2 sm:p-3 bg-white rounded-lg border-2 border-red-200 shadow-sm">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-red-100 border-2 border-red-400 flex-shrink-0"></div>
          <span className="text-gray-700"><strong>Critical Zone:</strong> Needs significant improvement</span>
        </div>
      </div>
    </div>
  );
};

ReadinessAttractivenessChart.propTypes = {
  assessment: PropTypes.object.isRequired,
  isChartHovered: PropTypes.bool.isRequired,
  setIsChartHovered: PropTypes.func.isRequired,
  getScoreColor: PropTypes.func.isRequired
};

export default ReadinessAttractivenessChart;
